import { DateTime } from '@calendly/datetime';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { AiNotetakerDuotoneIcon, CalendarIcon, TimeClockCircleIcon } from '@calendly/ui/components/icons';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorIconMeetingIntelligence } from '@calendly/ui/theme';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentContactProfile, useFetchContactByEmailQuery } from 'features/contacts';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { timeNotationSelector, timezoneSelector } from 'selectors/current_user';
import { formatDateRelative, formatTimeRange, getAttendeeIdentifier, getMatchTypeFilters, HighlightedText } from '../utils';
import { MoreResultsButton } from './more_results_button';
import { Dot } from './styles';
import { TranscriptMatch } from './transcript_match';
import { mapChangedFiltersForAnalytics } from '../../utils';
const containerIsPhoneCss = "czfv1l3";
const containerSelectedCss = "c1tsoi8m";
const containerCss = "c1frs026";
const Container = forwardRef((props, ref) => {
  const {
    selected,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone,
    [containerSelectedCss]: selected
  }, props.className)} />;
});
const meetingInfoIsPhoneCss = "m1nq3uv3";
const meetingInfoCss = "m171chzz";
const MeetingInfo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(meetingInfoCss, {
    [meetingInfoIsPhoneCss]: isPhone
  }, props.className)} />;
});
const fullWidthCss = "f1ym8usn";
const FullWidth = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(fullWidthCss, props.className)} />;
});
const dateTimeContainerCss = "d17wpqqj";
const DateTimeContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dateTimeContainerCss, props.className)} />;
});
const transcriptContainerIsPhoneCss = "t1ngapzy";
const transcriptContainerCss = "t14vqiq9";
const TranscriptContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(transcriptContainerCss, {
    [transcriptContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const buttonCss = "bliggeg";
const Button = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(buttonCss, props.className)} />);
const styledMeetingNameCss = "s7ct2jm";
const StyledMeetingName = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(styledMeetingNameCss, props.className)} />;
});
const findMatchingAttendee = (attendees, highlightedNames) => {
  for (const highlight of highlightedNames) {
    const matchedTerms = [];
    const emRegex = /<em>(.*?)<\/em>/g;
    let match = emRegex.exec(highlight);
    while (match !== null) {
      matchedTerms.push(match[1]);
      match = emRegex.exec(highlight);
    }
    const plainHighlight = highlight.replace(/<\/?em>/g, '');
    const matchResult = attendees.findIndex(attendeeName => plainHighlight === attendeeName || attendeeName.includes(plainHighlight) || plainHighlight.includes(attendeeName) || matchedTerms.some(term => attendeeName.toLowerCase().includes(term.toLowerCase())));
    if (matchResult !== -1) {
      return {
        matchingAttendee: attendees[matchResult],
        matchingHighlight: highlight,
        attendeeIndex: matchResult
      };
    }
  }
  return null;
};
const useAttendeesText = (meeting, position, resultsCount, searchQuery, filters, changedFilters) => {
  const dispatch = useDispatch();
  const track = useTrack();
  const attendee = meeting.attendees?.[0];
  const {
    data: contact
  } = useFetchContactByEmailQuery(attendee?.email?.toLowerCase(), {
    skip: meeting.attendees.length !== 1
  });
  if (isEmpty(meeting.attendees)) return null;
  const openContact = () => {
    if (!contact) return;
    track({
      name: TRACKING_EVENT_NAMES.SEARCH_INTERACTION,
      properties: {
        search_text: searchQuery,
        search_type: 'text',
        result_count: resultsCount,
        location: 'meetings_page',
        action: 'click_result_invitee',
        filters: mapChangedFiltersForAnalytics(changedFilters),
        match_type: getMatchTypeFilters(filters),
        semantic_search: 'include_related_terms',
        position
      }
    });
    dispatch(setCurrentContactProfile(contact));
  };

  // For single attendee case
  if (meeting.attendees.length === 1) {
    const highlightedAttendeeName = meeting.highlight?.attendees_name?.[0];
    const attendeeIdentifier = getAttendeeIdentifier(attendee);
    if (meeting.guests.length === 0) {
      return HtmlParser(I18n.t('publisher.meetings.search.results.attendees.single', {
        name: attendeeIdentifier
      }), {
        Attendee: () => <Button onClick={openContact}>
              {highlightedAttendeeName ? <HighlightedText html={highlightedAttendeeName} /> : attendeeIdentifier}
            </Button>
      });
    }
    return HtmlParser(I18n.t('publisher.meetings.search.results.attendees.with_guests', {
      name: attendeeIdentifier,
      count: meeting.guests.length
    }), {
      Attendee: () => <Button onClick={openContact}>
            {highlightedAttendeeName ? <HighlightedText html={highlightedAttendeeName} /> : attendeeIdentifier}
          </Button>,
      Guest: ({
        children
      }) => <Tooltip text={HtmlParser(meeting.guests.map(({
        email
      }) => email).join('<br/>').trim())}>
            <Button>{children}</Button>
          </Tooltip>
    });
  }

  // Multiple attendees case
  const attendees = meeting.attendees.map(getAttendeeIdentifier);
  const attendeesTooltip = attendees.join('<br/>');
  const highlightedAttendeeNames = meeting.highlight?.attendees_name || [];
  const highlightedEmails = meeting.highlight?.attendees_email || [];
  const allHighlights = [...highlightedAttendeeNames, ...highlightedEmails];
  const matchingAttendeeInfo = findMatchingAttendee(attendees, allHighlights);
  if (matchingAttendeeInfo) {
    const {
      matchingAttendee,
      matchingHighlight
    } = matchingAttendeeInfo;
    const remainingAttendees = attendees.filter(name => name !== matchingAttendee);
    const remainingTooltip = remainingAttendees.join('<br/>');
    return <>
        <HighlightedText html={matchingHighlight} />
        {' '}
        {HtmlParser(I18n.t('publisher.meetings.search.results.attendees.multiple', {
        count: meeting.attendees.length - 1
      }), {
        Attendee: ({
          children
        }) => <Tooltip text={HtmlParser(remainingTooltip)}>
                <Button>{children}</Button>
              </Tooltip>
      })}
      </>;
  }

  // Default case: no highlighted attendees
  return HtmlParser(I18n.t('publisher.meetings.search.results.attendees.multiple', {
    count: meeting.attendees.length
  }), {
    Attendee: ({
      children
    }) => <Tooltip text={HtmlParser(attendeesTooltip)}>
          <Button>{children}</Button>
        </Tooltip>
  });
};
const TranscriptMatches = ({
  matches,
  trackedMeetingUuid,
  query,
  resultCount,
  changedFilters,
  filters,
  position,
  eventUuid
}) => {
  const track = useTrack();
  if (matches.length === 0) {
    return null;
  }
  const matchesToShow = matches.slice(0, 2);
  const moreMatches = matches.length - matchesToShow.length;
  const handleClick = (e, url, action) => {
    track({
      name: TRACKING_EVENT_NAMES.SEARCH_INTERACTION,
      properties: {
        search_text: query,
        search_type: 'text',
        result_count: resultCount,
        location: 'meetings_search_results',
        action,
        filters: mapChangedFiltersForAnalytics(changedFilters),
        match_type: getMatchTypeFilters(filters),
        semantic_search: 'include_related_terms',
        position
      }
    });
    if (action === 'click_result_transcript') {
      track({
        name: TRACKING_EVENT_NAMES.CTA_CLICKED,
        properties: {
          cta_text: action,
          cta_key: 'meetings_search_results',
          cta_destination: 'meeting_recap',
          action: 'view_recap',
          event_uuid: eventUuid,
          mi_event_uuid: trackedMeetingUuid
        }
      });
    }
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  };
  return <TranscriptContainer>
      {matchesToShow.map(match => <TranscriptMatch key={match.timestamp} trackedMeetingUuid={trackedMeetingUuid} match={match} query={query} handleClick={handleClick} />)}
      {moreMatches > 0 && <MoreResultsButton count={moreMatches} trackedMeetingUuid={trackedMeetingUuid} query={query} handleClick={handleClick} />}
    </TranscriptContainer>;
};
const processPassages = passages => {
  if (!passages?.edges) return [];
  return passages.edges.map(edge => {
    if (!edge?.node || !edge.node.passage_highlight) return null;
    const {
      passage_highlight: {
        best_window_starting_char: bestWindowStartingChar,
        highlighted_words: highlightedWords
      },
      start_time: startTime,
      speaker,
      passage_index: passageIndex
    } = edge.node;
    if (!highlightedWords?.includes('<em>')) return null;
    const date = DateTime.fromMillis(Math.floor(startTime * 1000), {
      zone: 'utc'
    });
    const timestamp = date.hour > 0 ? date.toFormat('HH:mm:ss') : date.toFormat('mm:ss');
    const startIndex = Math.max(0, bestWindowStartingChar - 15);
    const prefix = startIndex > 0 ? '... ' : '';
    const visibleText = prefix + highlightedWords.substring(startIndex);
    return {
      timestamp,
      startTime,
      name: speaker?.name,
      text: visibleText,
      passageIndex
    };
  }).filter(Boolean);
};
export const Card = ({
  meeting,
  selected,
  onSelect,
  position,
  resultsCount,
  searchQuery,
  filters,
  changedFilters
}) => {
  const timeNotation = useSelector(timeNotationSelector);
  const timezone = useSelector(timezoneSelector);
  const isPhone = useDeviceType('isPhone');
  const attendeesText = useAttendeesText(meeting, position, resultsCount, searchQuery, filters, changedFilters);
  const date = formatDateRelative(meeting.startTime, timezone);
  const time = formatTimeRange(meeting.startTime, meeting.endTime, timezone, timeNotation);
  const hasMI = meeting.hasMeetingIntelligence;
  const highlightedMeetingName = meeting.highlight?.meeting_name?.[0];
  return <Container selected={selected}>
      <MeetingInfo>
        <Dot color={meeting.color} />
        <FullWidth>
          <Button onClick={onSelect}>
            <BodyText weight="bold">
              <StyledMeetingName>
                {highlightedMeetingName ? <HighlightedText html={highlightedMeetingName} /> : meeting.name}
              </StyledMeetingName>
            </BodyText>
          </Button>
          <BodyText size="medium">
            {attendeesText}
          </BodyText>
          <DateTimeContainer>
            <BodyText size="medium" color="muted">
              <CalendarIcon spacingRight={4} />
              {date}
            </BodyText>
            <BodyText size="medium" color="muted">
              <TimeClockCircleIcon spacingRight={4} />
              {time}
            </BodyText>
          </DateTimeContainer>
        </FullWidth>
        {hasMI && isPhone && <AiNotetakerDuotoneIcon color={colorIconMeetingIntelligence} />}
      </MeetingInfo>
      <TranscriptMatches matches={processPassages(meeting.passages)} trackedMeetingUuid={meeting.trackedMeetingUuid} eventUuid={meeting.uuid} query={searchQuery} changedFilters={changedFilters} filters={filters} position={position} resultCount={resultsCount} />
      {hasMI && !isPhone && <AiNotetakerDuotoneIcon color={colorIconMeetingIntelligence} />}
    </Container>;
};

require("./card.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./card.js");